import * as React from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import { FeeRule, computeFee } from './business/cart';
import { NumberField, currency } from './ui';

const KINDS: { type: FeeRule['type']; label: string }[] = [
  { type: 'none', label: 'Sin cargo' },
  { type: 'fixed', label: 'Cuota fija' },
  { type: 'tiers', label: 'Por rangos del total' },
  { type: 'percent', label: 'Porcentaje' },
  { type: 'per_item', label: 'Por pieza' },
];

const start = (type: FeeRule['type']): FeeRule =>
  type === 'fixed' ? { type, amount: 30 }
    : type === 'per_item' ? { type, amount: 5 }
      : type === 'percent' ? { type, percent: 10, min: null, max: null }
        : type === 'tiers' ? { type, tiers: [{ from: 0, amount: 50 }, { from: 1000, amount: 100 }] }
          : { type: 'none' };

/**
 * How a fee is charged from an order's total (delivery, or a service fee): pick a kind, fill its few fields and see it
 * on example orders. Same rules as Receipt-Web-Server api/fees.py.
 */
const FeeRuleEditor = ({ rule, onChange, disabled, examples = [250, 1250, 4000], noun = 'cargo' }: {
  rule: FeeRule; onChange: (rule: FeeRule) => void; disabled?: boolean; examples?: number[]; noun?: string;
}) => {
  const set = (change: Partial<FeeRule>) => onChange({ ...rule, ...change } as FeeRule);
  return (
    <Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {KINDS.map(kind => (
          <Chip key={kind.type} label={kind.label} disabled={disabled} color={rule.type === kind.type ? 'primary' : 'default'}
            variant={rule.type === kind.type ? 'filled' : 'outlined'} onClick={() => rule.type !== kind.type && onChange(start(kind.type))} />
        ))}
      </Box>

      <Stack spacing={1.5} sx={{ mt: 2 }}>
        {(rule.type === 'fixed' || rule.type === 'per_item') && (
          <NumberField size="small" label={rule.type === 'fixed' ? 'Monto por pedido ($)' : 'Monto por pieza ($)'} value={rule.amount}
            disabled={disabled} sx={{ maxWidth: 240 }} onChange={v => set({ amount: v ?? 0 })} />
        )}
        {rule.type === 'percent' && (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <NumberField size="small" label="% del total" value={rule.percent} disabled={disabled} onChange={v => set({ percent: v ?? 0 })} />
            <NumberField size="small" label="Mínimo $ (opcional)" value={rule.min ?? null} disabled={disabled} onChange={v => set({ min: v })} />
            <NumberField size="small" label="Máximo $ (opcional)" value={rule.max ?? null} disabled={disabled} onChange={v => set({ max: v })} />
          </Stack>
        )}
        {rule.type === 'tiers' && (
          <Stack spacing={1}>
            {rule.tiers.map((tier, i) => (
              <Stack key={i} direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <NumberField size="small" label="Desde ($)" value={i === 0 ? 0 : tier.from} disabled={disabled || i === 0} sx={{ width: 130 }}
                  onChange={v => set({ tiers: rule.tiers.map((t, j) => (j === i ? { ...t, from: v ?? 0 } : t)) })} />
                <TextField select size="small" label="Cobra" value={tier.percent != null ? 'percent' : 'amount'} disabled={disabled} sx={{ width: 140 }}
                  onChange={e => set({ tiers: rule.tiers.map((t, j) => (j === i
                    ? (e.target.value === 'percent' ? { from: t.from, percent: 5 } : { from: t.from, amount: 50 }) : t)) })}>
                  <MenuItem value="amount">Monto</MenuItem>
                  <MenuItem value="percent">Porcentaje</MenuItem>
                </TextField>
                <NumberField size="small" label={tier.percent != null ? '%' : '$'} value={tier.percent ?? tier.amount ?? 0} disabled={disabled} sx={{ width: 110 }}
                  onChange={v => set({ tiers: rule.tiers.map((t, j) => (j === i ? (t.percent != null ? { ...t, percent: v ?? 0 } : { ...t, amount: v ?? 0 }) : t)) })} />
                {rule.tiers.length > 1 && !disabled && (
                  <Tooltip title="Quitar rango">
                    <IconButton size="small" onClick={() => set({ tiers: rule.tiers.filter((_, j) => j !== i) })}><DeleteOutlineIcon fontSize="small" /></IconButton>
                  </Tooltip>
                )}
              </Stack>
            ))}
            {!disabled && (
              <Button size="small" startIcon={<AddIcon />} sx={{ alignSelf: 'flex-start' }}
                onClick={() => set({ tiers: [...rule.tiers, { from: Math.max(...rule.tiers.map(t => t.from)) + 1000, amount: 0 }] })}>
                Agregar rango
              </Button>
            )}
          </Stack>
        )}
        {rule.type !== 'none' && (
          <TextField select size="small" label="Redondeo" value={rule.round_to ?? 0} disabled={disabled} sx={{ maxWidth: 240 }}
            onChange={e => set({ round_to: Number(e.target.value) || undefined })}>
            <MenuItem value={0}>Sin redondeo</MenuItem>
            <MenuItem value={1}>Al peso</MenuItem>
            <MenuItem value={5}>A múltiplos de $5</MenuItem>
            <MenuItem value={10}>A múltiplos de $10</MenuItem>
          </TextField>
        )}
      </Stack>

      <Box data-testid="fee-examples" sx={{ mt: 2, p: 1.5, borderRadius: 2, bgcolor: 'rgba(20,37,44,0.04)' }}>
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Así se cobra</Typography>
        {examples.map(total => {
          const fee = computeFee(rule, total, 3);
          return (
            <Typography key={total} variant="body2" sx={{ color: 'text.secondary' }}>
              Un pedido de {currency(total)}{rule.type === 'per_item' ? ' (3 piezas)' : ''} → {noun} {fee ? currency(fee) : 'sin costo'}, total {currency(total + fee)}
            </Typography>
          );
        })}
      </Box>
    </Box>
  );
};

export default FeeRuleEditor;
